import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsPlacement, fmtMoney, fmtDate } from './api';

const PLACEMENT_STATUSES = ['pending', 'active', 'completed', 'fell_through'];
const INVOICE_STATUSES = ['not_invoiced', 'invoiced', 'paid'];

const TYPE_CHIP: Record<string, string> = {
  contingent: 'bg-blue-50 text-blue-600',
  retained: 'bg-amber-50 text-amber-700',
  contract: 'bg-violet-50 text-violet-600',
};

const bookedOf = (pl: AtsPlacement) =>
  (pl.milestones || []).reduce((s, m) => s + (m.booked ? Number(m.amount || 0) : 0), 0);

const AtsPlacements: React.FC = () => {
  const { token } = useAuth();
  const [placements, setPlacements] = useState<AtsPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    atsFetch(token, '/placements')
      .then((d) => setPlacements(d.placements))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const patch = async (id: number, body: any) => {
    if (!token) return;
    await atsFetch(token, `/placements/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).catch((e: any) => setError(e.message));
    load();
  };

  const toggleMilestone = async (pl: AtsPlacement, idx: number) => {
    if (!token || !pl.milestones) return;
    const milestones = pl.milestones.map((m, i) => (i === idx ? { ...m, booked: !m.booked } : m));
    await atsFetch(token, `/placements/${pl.id}`, { method: 'PATCH', body: JSON.stringify({ milestones }) }).catch((e: any) => setError(e.message));
    load();
  };

  const booked = placements.filter((p) => p.status !== 'fell_through');
  // Retained books per ticked milestone; contingent/contract book their fee.
  const totalFees = booked.reduce((s, p) => s + (p.placement_type === 'retained' ? bookedOf(p) : Number(p.fee_amount || 0)), 0);

  return (
    <div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-medium text-gray-500">Placements</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{booked.length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-medium text-gray-500">Fees booked</p>
          <p className="text-2xl font-semibold text-emerald-600 mt-1">{fmtMoney(totalFees)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
          <p className="text-xs font-medium text-gray-500">Awaiting invoice</p>
          <p className="text-2xl font-semibold text-gray-900 mt-1">{booked.filter((p) => p.invoice_status === 'not_invoiced').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>
      ) : placements.length === 0 ? (
        <div className="py-16 text-center">
          <p className="text-sm text-gray-500 mb-1">No placements yet.</p>
          <p className="text-xs text-gray-400">Drag a candidate into your Placed stage and the placement dialog opens with the role's fee terms prefilled.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="px-5 py-2.5 font-medium">Candidate</th>
                <th className="px-3 py-2.5 font-medium">Role</th>
                <th className="px-3 py-2.5 font-medium">Client</th>
                <th className="px-3 py-2.5 font-medium">Type</th>
                <th className="px-3 py-2.5 font-medium">Start</th>
                <th className="px-3 py-2.5 font-medium text-right">Fee</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-5 py-2.5 font-medium">Invoice</th>
              </tr>
            </thead>
            <tbody>
              {placements.map((pl) => (
                <React.Fragment key={pl.id}>
                <tr className={`border-b border-gray-50 ${pl.status === 'fell_through' ? 'opacity-60' : ''}`}>
                  <td className="px-5 py-3">
                    {pl.person_id ? (
                      <Link to={`/ats/people/${pl.person_id}`} className="font-medium text-gray-900 hover:text-blue-700">{pl.person_name || pl.live_person_name}</Link>
                    ) : (
                      <span className="font-medium text-gray-900">{pl.person_name || '—'}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-gray-600">{pl.role_title || pl.live_role_title || '—'}</td>
                  <td className="px-3 py-3 text-gray-600">
                    {pl.client_id
                      ? <Link to={`/ats/clients/${pl.client_id}`} className="hover:text-blue-700">{pl.client_name || pl.live_client_name}</Link>
                      : (pl.client_name || '—')}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize ${TYPE_CHIP[pl.placement_type] || TYPE_CHIP.contingent}`}>{pl.placement_type}</span>
                  </td>
                  <td className="px-3 py-3 text-xs text-gray-500">{fmtDate(pl.start_date)}</td>
                  <td className={`px-3 py-3 text-right font-medium ${pl.status === 'fell_through' ? 'line-through text-gray-400' : 'text-emerald-600'}`}>
                    {pl.placement_type === 'retained' ? (
                      <span title="Booked / total">{fmtMoney(bookedOf(pl), pl.currency)} <span className="text-gray-400 font-normal">/ {fmtMoney(pl.fee_amount, pl.currency)}</span></span>
                    ) : (
                      fmtMoney(pl.fee_amount, pl.currency)
                    )}
                    {pl.placement_type === 'contract' && pl.charge_rate != null && pl.pay_rate != null && (
                      <span className="block text-[10px] text-gray-400 font-normal">{fmtMoney(pl.charge_rate, pl.currency)} / {fmtMoney(pl.pay_rate, pl.currency)} per {pl.rate_unit || 'day'}</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <select value={pl.status} onChange={(e) => patch(pl.id, { status: e.target.value })} className="text-xs border border-gray-200 rounded-md px-1.5 py-1 text-gray-600 capitalize">
                      {PLACEMENT_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3">
                    <select value={pl.invoice_status} onChange={(e) => patch(pl.id, { invoiceStatus: e.target.value })} className="text-xs border border-gray-200 rounded-md px-1.5 py-1 text-gray-600 capitalize">
                      {INVOICE_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                    </select>
                  </td>
                </tr>
                {pl.placement_type === 'retained' && (pl.milestones || []).length > 0 && (
                  <tr className="border-b border-gray-50 bg-amber-50/30">
                    <td colSpan={8} className="px-5 py-2">
                      <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                        {(pl.milestones || []).map((m, i) => (
                          <label key={i} className="flex items-center gap-1.5 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              checked={m.booked}
                              onChange={() => toggleMilestone(pl, i)}
                              className="rounded border-gray-300 text-amber-600"
                            />
                            <span className={m.booked ? 'text-gray-700' : 'text-gray-400'}>{m.label}</span>
                            <span className={`font-medium ${m.booked ? 'text-emerald-600' : 'text-gray-400'}`}>{fmtMoney(m.amount, pl.currency)}</span>
                            {m.booked && m.booked_at && <span className="text-[10px] text-gray-400">booked {fmtDate(m.booked_at)}</span>}
                          </label>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AtsPlacements;
